// ./src/components/guards/OnboardingGuard.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { shouldBypassOnboarding } from "@/utils/onboarding";

import { useApp } from "@/contexts/AppContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { LoadingOverlay } from "@/components/generic/LoadingOverlay";
import { ErrorState } from "@/components/generic/ErrorState";

// ============================================
// Types
// ============================================

export interface OnboardingGuardProps {
  children: React.ReactNode;
}

// ============================================
// Inner component that uses context
// ============================================

const OnboardingGuardInner: React.FC<OnboardingGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Profile context - uses 'loading' (from UseUserProfileReturn)
  const {
    isLoading: profileLoading,
    isLoaded: profileLoaded,
    profileExists,
    lastError: profileError,
    refresh: refreshProfile,
  } = useUserProfileContext();

  // App essentials context - uses 'loading' (from UseAppEssentialsReturn)
  const {
    isLoading: essentialsLoading,
    isLoaded: essentialsLoaded,
    lastError: essentialsError,
    refresh: refreshEssentials,
  } = useAppEssentialsContext();

  // Track if we've completed the initial check
  const [hasChecked, setHasChecked] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Combined loading state
  const isLoading = profileLoading || essentialsLoading;
  const isFullyLoaded = profileLoaded && essentialsLoaded;

  // Check for errors that should show error state
  const hasProfileError = profileError && profileExists === null;
  const hasEssentialsError = !!essentialsError;
  const hasDisplayableError = hasProfileError || hasEssentialsError;

  const handleRetry = async () => {
    setIsRetrying(true);

    // Retry both if needed
    const promises: Promise<unknown>[] = [];

    if (profileError || !profileLoaded) {
      promises.push(refreshProfile());
    }

    if (essentialsError || !essentialsLoaded) {
      promises.push(refreshEssentials());
    }

    await Promise.all(promises);
    setIsRetrying(false);
  };

  useEffect(() => {
    // Wait until both profile and essentials loading is complete
    if (isLoading || !isFullyLoaded) {
      return;
    }

    // Check if current route should bypass onboarding
    if (shouldBypassOnboarding(location.pathname)) {
      setHasChecked(true);
      return;
    }

    // If there was an error loading the profile, don't redirect
    // We can't determine onboarding status when profileExists is unknown
    if (profileError || profileExists === null) {
      setHasChecked(true);
      return;
    }

    // Only redirect if profile definitively doesn't exist (NOT_FOUND response)
    if (profileExists === false) {
      navigate("/onboarding", { replace: true });
      return;
    }

    // Profile exists and essentials loaded - user can proceed
    setHasChecked(true);
  }, [
    isLoading,
    isFullyLoaded,
    profileExists,
    profileError,
    location.pathname,
    navigate,
  ]);

  // Show loading while checking onboarding status or loading essentials
  if (isLoading || !isFullyLoaded) {
    return <LoadingOverlay visible loadingText="Loading essentials" />;
  }

  // Show error state if profile or essentials loading failed
  if (hasDisplayableError) {
    const errorTitle = hasProfileError
      ? "Unable to load profile"
      : "Unable to load application data";
    const errorMessage = hasProfileError
      ? profileError || "We couldn't load your profile. Please check your connection and try again."
      : essentialsError || "We couldn't load application data. Please check your connection and try again.";

    return (
      <ErrorState
        title={errorTitle}
        message={errorMessage}
        onRetry={handleRetry}
        retrying={isRetrying}
      />
    );
  }

  // Still checking (waiting for effect to complete)
  if (!hasChecked && !shouldBypassOnboarding(location.pathname)) {
    return <LoadingOverlay visible />;
  }

  return <>{children}</>;
};

// ============================================
// Main component - checks bypass BEFORE using context
// ============================================

/**
 * OnboardingGuard checks if the user has completed onboarding and ensures
 * app essentials are loaded before rendering authenticated content.
 *
 * If the user hasn't completed onboarding, it redirects them to the onboarding page.
 *
 * This guard should wrap the authenticated app content to ensure:
 * 1. New users complete their profile setup before accessing the app
 * 2. App essentials (lookup data) are loaded before rendering pages that need them
 *
 * Important: The guard only redirects to onboarding when the profile is definitively
 * not found (NOT_FOUND response). If an error occurs during profile loading
 * (network issues, API errors), it shows an error state with retry option
 * instead of incorrectly redirecting to onboarding.
 */
export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const { isAuthenticated } = useApp();

  // For bypass routes OR unauthenticated users, skip onboarding check entirely
  if (shouldBypassOnboarding(location.pathname) || !isAuthenticated) {
    return <>{children}</>;
  }

  // For authenticated users on non-bypass routes, use the full guard
  return <OnboardingGuardInner>{children}</OnboardingGuardInner>;
};